import React, { useState } from 'react';

interface DeviceCapabilities {
  resolutions?: Array<{ width: number; height: number }>;
  frameRates?: number[];
  codecs?: string[];
  audioSupport?: boolean;
  motionDetection?: boolean;
  panTiltZoom?: boolean;
  nightVision?: boolean;
  infrared?: boolean;
}

interface DeviceNetworkInfo {
  macAddress?: string;
  networkInterface?: string;
  subnet?: string;
  gateway?: string;
  dns?: string[];
  connectionType?: 'wired' | 'wireless';
  signalStrength?: number;
}

interface DeviceStatus {
  firmwareVersion?: string;
  hardwareVersion?: string;
  uptime?: number;
  temperature?: number;
  memoryUsage?: number;
  cpuUsage?: number;
  diskUsage?: number;
  lastReboot?: number;
}

interface StreamMetrics {
  bitrate?: number;
  packetLoss?: number;
  latency?: number;
  jitter?: number;
  frameRate?: number;
  resolution?: { width: number; height: number };
  codec?: string;
  lastMetricsUpdate?: number;
}

interface DeviceSecurity {
  authenticationMethods?: string[];
  encryptionSupport?: string[];
  certificateInfo?: {
    issuer?: string;
    subject?: string;
    validFrom?: number;
    validTo?: number;
  };
  accessLevel?: 'admin' | 'operator' | 'user';
}

interface DeviceInfo {
  urn: string;
  ipAddress: string;
  hostname: string;
  port: number;
  model: string;
  manufacturer?: string;
  serialNumber?: string;
  lastSeen: number;
  isOnline: boolean;
  rtspUrl?: string;
  capabilities?: DeviceCapabilities;
  networkInfo?: DeviceNetworkInfo;
  status?: DeviceStatus;
  streamMetrics?: StreamMetrics;
  security?: DeviceSecurity;
  discoveryMethod?: 'onvif' | 'manual' | 'imported';
  firstDiscovered?: number;
  lastVerified?: number;
  verificationFailures?: number;
}

interface CameraInfoCardProps {
  deviceInfo: DeviceInfo;
  className?: string;
}

const CameraInfoCard: React.FC<CameraInfoCardProps> = ({ deviceInfo, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatUptime = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    // Handle both seconds and milliseconds timestamps
    const date = timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    const date = timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return 'N/A';
    
    const now = Date.now();
    const diff = now - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };


  return (
    <div className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-4 border border-gray-700 shadow-lg ${className}`}>
      {/* Header - Always visible */}
      <div 
        className="flex items-center justify-between cursor-pointer hover:bg-gray-700 hover:bg-opacity-30 rounded p-2 -m-2 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className={`w-4 h-4 rounded-full ${deviceInfo.isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <div className={`absolute inset-0 w-4 h-4 rounded-full ${deviceInfo.isOnline ? 'bg-green-500' : 'bg-red-500'} opacity-25 animate-ping`} />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">{deviceInfo.model}</h3>
            <p className="text-gray-400 text-sm flex items-center gap-2">
              <span>📍 {deviceInfo.ipAddress}:{deviceInfo.port}</span>
              <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">{formatRelativeTime(deviceInfo.lastSeen)}</span>
            </p>
          </div>
        </div>
        <button className="text-gray-400 hover:text-white transition-colors">
          <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Quick Info Badges - Always visible */}
      <div className="mt-3 flex flex-wrap gap-2">
        {deviceInfo.manufacturer && (
          <div className="bg-blue-600 bg-opacity-20 border border-blue-500 text-blue-300 px-3 py-1 rounded-full text-xs font-medium">
            🏭 {deviceInfo.manufacturer}
          </div>
        )}
        {deviceInfo.serialNumber && (
          <div className="bg-purple-600 bg-opacity-20 border border-purple-500 text-purple-300 px-3 py-1 rounded-full text-xs font-medium">
            🔢 {deviceInfo.serialNumber}
          </div>
        )}
        {deviceInfo.status?.firmwareVersion && (
          <div className="bg-green-600 bg-opacity-20 border border-green-500 text-green-300 px-3 py-1 rounded-full text-xs font-medium">
            💾 {deviceInfo.status.firmwareVersion.split('.').slice(0, 3).join('.')}
          </div>
        )}
        {deviceInfo.discoveryMethod && (
          <div className="bg-gray-600 bg-opacity-20 border border-gray-500 text-gray-300 px-3 py-1 rounded-full text-xs font-medium uppercase">
            📡 {deviceInfo.discoveryMethod}
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Device Status */}
          {deviceInfo.status && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <span className="text-green-400">📊</span>
                Device Status
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {deviceInfo.status.firmwareVersion && (
                  <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                    <div className="text-gray-400 text-xs mb-1">Firmware</div>
                    <div className="text-white font-mono text-xs truncate" title={deviceInfo.status.firmwareVersion}>
                      {deviceInfo.status.firmwareVersion}
                    </div>
                  </div>
                )}
                {deviceInfo.status.uptime !== undefined && (
                  <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                    <div className="text-gray-400 text-xs mb-1">⏱️ Uptime</div>
                    <div className="text-green-400 font-semibold">{formatUptime(deviceInfo.status.uptime)}</div>
                  </div>
                )}
                {deviceInfo.status.temperature !== undefined && (
                  <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                    <div className="text-gray-400 text-xs mb-1">🌡️ Temperature</div>
                    <div className={`font-semibold ${deviceInfo.status.temperature > 70 ? 'text-red-400' : 'text-blue-400'}`}>
                      {deviceInfo.status.temperature}°C
                    </div>
                  </div>
                )}
                {deviceInfo.status.memoryUsage !== undefined && (
                  <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                    <div className="text-gray-400 text-xs mb-1">💾 Memory</div>
                    <div className={`font-semibold ${deviceInfo.status.memoryUsage > 80 ? 'text-red-400' : 'text-cyan-400'}`}>
                      {deviceInfo.status.memoryUsage}%
                    </div>
                  </div>
                )}
                {deviceInfo.status.cpuUsage !== undefined && (
                  <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                    <div className="text-gray-400 text-xs mb-1">⚙️ CPU</div>
                    <div className={`font-semibold ${deviceInfo.status.cpuUsage > 80 ? 'text-red-400' : 'text-cyan-400'}`}>
                      {deviceInfo.status.cpuUsage}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Capabilities */}
          {(deviceInfo.capabilities || (deviceInfo as any).profiles) && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <span className="text-blue-400">⚡</span>
                Capabilities
              </h4>
              <div className="flex flex-wrap gap-2">
                {deviceInfo.capabilities?.audioSupport && (
                  <span className="px-3 py-1.5 bg-blue-600 bg-opacity-30 border border-blue-500 text-blue-300 text-xs rounded-lg font-medium">🔊 Audio</span>
                )}
                {/* Check for PTZ in profiles data */}
                {((deviceInfo as any).profiles?.some((p: any) => p.PTZConfiguration) || deviceInfo.capabilities?.panTiltZoom) && (
                  <span className="px-3 py-1.5 bg-purple-600 bg-opacity-30 border border-purple-500 text-purple-300 text-xs rounded-lg font-medium">🎯 PTZ Control</span>
                )}
                {deviceInfo.capabilities?.nightVision && (
                  <span className="px-3 py-1.5 bg-indigo-600 bg-opacity-30 border border-indigo-500 text-indigo-300 text-xs rounded-lg font-medium">🌙 Night Vision</span>
                )}
                {deviceInfo.capabilities?.motionDetection && (
                  <span className="px-3 py-1.5 bg-green-600 bg-opacity-30 border border-green-500 text-green-300 text-xs rounded-lg font-medium">👁️ Motion Detection</span>
                )}
                {deviceInfo.capabilities?.infrared && (
                  <span className="px-3 py-1.5 bg-red-600 bg-opacity-30 border border-red-500 text-red-300 text-xs rounded-lg font-medium">🔴 Infrared</span>
                )}
              </div>
              {deviceInfo.capabilities?.resolutions && deviceInfo.capabilities.resolutions.length > 0 && (
                <div className="mt-3 p-2 bg-gray-700 bg-opacity-30 rounded text-sm">
                  <span className="text-gray-400 font-medium">📐 Resolutions:</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {deviceInfo.capabilities.resolutions.map((r, i) => (
                      <span key={i} className="text-white bg-gray-600 px-2 py-0.5 rounded text-xs">
                        {r.width}x{r.height}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Network Info */}
          {deviceInfo.networkInfo && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white font-semibold mb-2">Network</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {deviceInfo.networkInfo.macAddress && (
                  <div>
                    <span className="text-gray-400">MAC:</span>
                    <span className="text-white ml-2">{deviceInfo.networkInfo.macAddress}</span>
                  </div>
                )}
                {deviceInfo.networkInfo.connectionType && (
                  <div>
                    <span className="text-gray-400">Type:</span>
                    <span className="text-white ml-2 capitalize">{deviceInfo.networkInfo.connectionType}</span>
                  </div>
                )}
                {deviceInfo.networkInfo.gateway && (
                  <div>
                    <span className="text-gray-400">Gateway:</span>
                    <span className="text-white ml-2">{deviceInfo.networkInfo.gateway}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stream Metrics */}
          {deviceInfo.streamMetrics && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-white font-semibold mb-2">Stream Metrics</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {deviceInfo.streamMetrics.bitrate !== undefined && (
                  <div>
                    <span className="text-gray-400">Bitrate:</span>
                    <span className="text-white ml-2">{deviceInfo.streamMetrics.bitrate} kbps</span>
                  </div>
                )}
                {deviceInfo.streamMetrics.frameRate !== undefined && (
                  <div>
                    <span className="text-gray-400">FPS:</span>
                    <span className="text-white ml-2">{deviceInfo.streamMetrics.frameRate}</span>
                  </div>
                )}
                {deviceInfo.streamMetrics.resolution && (
                  <div>
                    <span className="text-gray-400">Resolution:</span>
                    <span className="text-white ml-2">
                      {deviceInfo.streamMetrics.resolution.width}x{deviceInfo.streamMetrics.resolution.height}
                    </span>
                  </div>
                )}
                {deviceInfo.streamMetrics.codec && (
                  <div>
                    <span className="text-gray-400">Codec:</span>
                    <span className="text-white ml-2">{deviceInfo.streamMetrics.codec}</span>
                  </div>
                )}
                {deviceInfo.streamMetrics.latency !== undefined && (
                  <div>
                    <span className="text-gray-400">Latency:</span>
                    <span className="text-white ml-2">{deviceInfo.streamMetrics.latency}ms</span>
                  </div>
                )}
                {deviceInfo.streamMetrics.packetLoss !== undefined && (
                  <div>
                    <span className="text-gray-400">Packet Loss:</span>
                    <span className="text-white ml-2">{deviceInfo.streamMetrics.packetLoss}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discovery Info */}
          <div className="border-t border-gray-700 pt-3">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="text-yellow-400">🔍</span>
              Discovery Info
            </h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {deviceInfo.firstDiscovered && (
                <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                  <div className="text-gray-400 mb-1">First Seen</div>
                  <div className="text-white">{formatTimestamp(deviceInfo.firstDiscovered)}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{formatRelativeTime(deviceInfo.firstDiscovered)}</div>
                </div>
              )}
              <div className="bg-gray-700 bg-opacity-30 p-2 rounded">
                <div className="text-gray-400 mb-1">Last Seen</div>
                <div className="text-white">{formatTimestamp(deviceInfo.lastSeen)}</div>
                <div className="text-green-400 text-xs mt-0.5">{formatRelativeTime(deviceInfo.lastSeen)}</div>
              </div>
              {deviceInfo.lastVerified && (
                <div className="bg-gray-700 bg-opacity-30 p-2 rounded col-span-2">
                  <div className="text-gray-400 mb-1">Last Verified</div>
                  <div className="text-white">{formatTimestamp(deviceInfo.lastVerified)}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{formatRelativeTime(deviceInfo.lastVerified)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraInfoCard;

